import {
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import HiddenDialogButton from '../../../components/Dialog/HiddenDialogButton';
import Button from '../../../components/styledComponents/Button';
import { RemoveButton } from '../../../components/styledComponents/IconButtons';
import { useDirectAccessAnalytics } from '../../../hooks/useAnalytics';

interface ClubRedirectDialogProps {
  open: boolean;
  closeDialog?: () => void;
  loginUrl: string;
  loginRedirect?: boolean;
  outOfTerritoryZipCode?: boolean;
}

const ClubRedirectDialog = ({
  closeDialog,
  open,
  loginUrl,
  loginRedirect,
  outOfTerritoryZipCode,
}: ClubRedirectDialogProps) => {
  const [redirectTimeout, setRedirectTimeout] = useState<number | null>(null);
  const { track } = useDirectAccessAnalytics();

  useEffect(() => {
    if (open) {
      track('Club Redirect Displayed', {
        event_type: 'Messaged',
        location: 'Feature Card',
      });
      setRedirectTimeout(
        window.setTimeout(() => window.location.assign(loginUrl), 20000) // redirect after 20 seconds
      );
    }
  }, [open]);

  return (
    <Dialog
      aria-labelledby="clubRedirectLabel"
      data-testid="club-redirect-dialog"
      open={open}
      onClose={closeDialog}
      sx={{ backdropFilter: 'blur(5px)' }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          margin: 0,
          px: { mobile: 3, tablet: 4 },
          py: { mobile: 3, tablet: 3 },
          position: { mobile: 'fixed', tablet: 'relative' },
          width: { mobile: 1, tablet: 480 },
          height: 'auto',
          maxHeight: 1,
        },
      }}
    >
      <HiddenDialogButton />
      {!loginRedirect && closeDialog && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{
            width: 1,
          }}
        >
          <RemoveButton
            aria-label="Close redirect dialog"
            data-testid="club-redirect-dialog-x-button"
            onClick={() => {
              if (typeof redirectTimeout === 'number') {
                clearTimeout(redirectTimeout);
              }
              closeDialog();
            }}
          />
        </Stack>
      )}
      {outOfTerritoryZipCode ? (
        <DialogTitle
          id="clubRedirectLabel"
          sx={{
            fontSize: 28,
            fontWeight: 700,
            textAlign: 'center',
            p: 0,
            my: { mobile: 2, tablet: 3 },
          }}
        >
          Find your local AAA Club
        </DialogTitle>
      ) : (
        <DialogTitle
          id="clubRedirectLabel"
          sx={{
            fontSize: 28,
            fontWeight: 700,
            textAlign: 'center',
            p: 0,
            my: { mobile: 2, tablet: 3 },
          }}
        >
          We&apos;re taking you to your AAA club
        </DialogTitle>
      )}
      {outOfTerritoryZipCode ? (
        <DialogContent
          sx={{
            px: 0,
            pb: 3,
          }}
        >
          <Stack direction="column">
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontWeight: 400,
                fontSize: 18,
                lineHeight: '24px',
              }}
            >
              AAA/CAA is a federation of regional clubs located throughout North
              America.
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontWeight: 400,
                fontSize: 18,
                lineHeight: '24px',
                mt: 2,
              }}
            >
              We do not service the zip code you&apos;ve entered, but we will
              redirect you to AAA.com to find your local AAA Club to take full
              advantage of its products and services.
            </Typography>
            <Button
              fullWidth
              data-testid="continue-to-club-button"
              variant="contained"
              sx={{
                whiteSpace: 'nowrap',
                height: { mobile: 36, tablet: 42 },
                mt: 5,
              }}
              onClick={() => {
                track('Club Accessed', {
                  event_type: 'Link Accessed',
                  location: 'Feature Card',
                });
                if (typeof redirectTimeout === 'number') {
                  clearTimeout(redirectTimeout);
                }
                window.location.assign(loginUrl);
              }}
            >
              Go to AAA.com
            </Button>
          </Stack>
        </DialogContent>
      ) : (
        <DialogContent
          sx={{
            px: 0,
            pb: 3,
          }}
        >
          <Stack direction="column">
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontWeight: 400,
                fontSize: 18,
                lineHeight: '24px',
              }}
            >
              We&apos;re redirecting you so you can access your membership and
              insurance in one place.
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Cabin',
                fontKerning: 'normal',
                fontWeight: 400,
                fontSize: 18,
                lineHeight: '24px',
                mt: 2,
              }}
            >
              If you haven&apos;t been redirected, select “Continue to my AAA
              club.”
            </Typography>
            <Button
              fullWidth
              data-testid="continue-to-club-button"
              variant="contained"
              sx={{
                whiteSpace: 'nowrap',
                height: { mobile: 36, tablet: 42 },
                mt: 5,
              }}
              onClick={() => {
                track('Club Accessed', {
                  event_type: 'Link Accessed',
                  location: 'Feature Card',
                });
                if (typeof redirectTimeout === 'number') {
                  clearTimeout(redirectTimeout);
                }
                window.location.assign(loginUrl);
              }}
            >
              Continue to my AAA club
            </Button>
          </Stack>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default ClubRedirectDialog;
